import { FC, RefObject, useImperativeHandle, useState } from 'react';

import { isFileMetaInfoDtoId } from '@/entities/file/helpers';
import {
  FilePath,
  useFileEncryptionUpload,
} from '@/entities/file/hooks/use-file-encryption-upload';
import { FileUploaderRef } from '@/widgets/FileUploader';
import { FileItemInfo } from '@/widgets/modals/containers/UploadFiles/types';
import { UploaderWidget } from '@/widgets/UploaderWidget';

type AssetPhotoUploaderProps = {
  files: FileItemInfo[];
  uploaderRef?: RefObject<FileUploaderRef>;
  isLoading?: boolean;
  onRemove: (id?: string) => void;
};

export const AssetPhotoUploader: FC<AssetPhotoUploaderProps> = ({
  files,
  uploaderRef,
  isLoading,
  onRemove,
}) => {
  const [uploadCandidates, setUploadCandidates] = useState<FileItemInfo[]>([]);
  const { encryptAndUploadFile, getFileMeta } = useFileEncryptionUpload({
    useDirectUrl: true,
  });

  useImperativeHandle(
    uploaderRef,
    (): FileUploaderRef => ({
      upload: async () => {
        const promises = uploadCandidates.reduce<Array<Promise<FilePath>>>((acc, c) => {
          if (!isFileMetaInfoDtoId(c.id)) {
            acc.push(encryptAndUploadFile(c.file));
          } else {
            acc.push(Promise.resolve(getFileMeta(c.id)?.filePath ?? ''));
          }

          return acc;
        }, []);

        const filePaths = await Promise.all(promises);

        return Promise.resolve({ success: true, filePaths });
      },
    }),
    [files, uploadCandidates],
  );

  return (
    <UploaderWidget
      isLoading={isLoading}
      onFilesSelect={setUploadCandidates}
      onRemove={onRemove}
      uploadedFiles={files}
    />
  );
};
